import { useAtom } from "jotai";
import { useEffect } from "react";
import { createPortal } from "react-dom";
import { textLoadFailedAtom, textReadyAtom } from "src/utils/languageService";
import { userSettledAtom } from "src/services/user/userService";
import { startupTexts } from "./startupTexts";
import classes from "./StartupView.module.scss";

/**
 * Shown from the first render until the app has heard back from the API: the language texts
 * and the first check for a logged-in user. Rendered next to the router in main.tsx, so it
 * also covers a deep link whose route loaders are still waiting.
 *
 * It is portalled to <body> so the app root can be made inert while it shows: the overlay
 * hides the unfinished app from the eye, `inert` hides it from the keyboard and screen readers.
 */
const StartupView = () => {
    const [textReady] = useAtom(textReadyAtom);
    const [textLoadFailed] = useAtom(textLoadFailedAtom);
    const [userSettled] = useAtom(userSettledAtom);

    const visible = !(textReady && userSettled);

    useEffect(() => {
        const root = document.getElementById("root");
        if (!root || !visible) return;
        root.inert = true;
        return () => { root.inert = false; };
    }, [visible]);

    if (!visible) return null;

    const t = startupTexts(navigator.languages?.length ? navigator.languages : [navigator.language ?? ""]);

    return createPortal(<div className={classes.startup} data-testid="startup-view">
        <div role="status" aria-live="polite" className="text-center">
            <img src="/imgs/logo-author.svg" alt="LäraNära Author" />
            <h1>{t.starting}</h1>
            {textLoadFailed
                ? <>
                    <p>{t.failed}</p>
                    <button type="button" className="btn btn-primary" onClick={() => window.location.reload()}>{t.retry}</button>
                </>
                : <>
                    <p>{t.waiting}</p>
                    <div className="spinner-border text-primary" aria-hidden="true" />
                </>
            }
        </div>
    </div>, document.body);
};

export default StartupView;
